import { TemplateRef } from '@angular/core';

/**
 * Model for vertical column table. Contains info how to display columns and data that needed to be displayed in the table.
 * Expected to be used within Migration UI only.
 *
 * @param T - is type of item
 */
export class TableModel<T> {
	private _columns: Array<ColumnModel<T>> = [];
	private _items: Array<T>;


	/**
	 * @param items to display in the table
	 */
	setItems(items: Array<T>): TableModel<T> {
		this._items = items;
		return this;
	}


	get items(): Array<T> {
		return this._items;
	}

	/**
	 * @param columnModel contains data how column header and how cells of specific column should be rendered.
	 */
	addColumn(columnModel: ColumnModel<T>): TableModel<T> {
		this.validate(columnModel);
		this._columns.push(columnModel);
		return this;
	}

	private validate(columnModel: ColumnModel<T>): void {
		if (!(columnModel.columnHeaderText || columnModel.columnHeaderTemplate)) {
			throw new Error('columnHeader or columnHeaderTemplate should be specified to configure the column.');
		}
		if (columnModel.columnHeaderText && columnModel.columnHeaderTemplate) {
			throw new Error('columnHeader and columnHeaderTemplate can\'t be used together. Use only one of them to configure the column.');
		}
		if (!(columnModel.getValueFunction || columnModel.cellTemplate)) {
			throw new Error('getValueFunction or cellTemplate should be specified to configure the column.');
		}
		if (columnModel.getValueFunction && columnModel.cellTemplate) {
			throw new Error('getValueFunction and cellTemplate can\'t be used together. Use only one of them to configure the column.');
		}
	}

	get columns(): Array<ColumnModel<T>> {
		return this._columns;
	}
}

export interface ColumnModel<T> {

	/**
	 * Column header text without any additional html inside column header cell.
	 */
	columnHeaderText?: string;

	/**
	 * ng-template ref of column header. Expected to be used when it's needed to include some additional html into column header cell.
	 * Use columnHeader field if it's needed to include only text to column header cell.
	 */
	columnHeaderTemplate?: TemplateRef<any>;

	/**
	 * Function to get some value of field the of item to display for the column as text without any additional html inside cell.
	 */
	getValueFunction?: (item: T) => any;

	/**
	 * ng-template ref of cell. Expected to be used when it's needed to include some additional html into cell.
	 * Expected to be parametrized with variable "item".
	 * Use getValueFunction or addColumn method if it's needed to include as text only some value of field of the item.
	 */
	cellTemplate?: TemplateRef<any>;
}
